import firebase from "firebase/app";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { FC, ReactNode } from "react";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { StackedAvatarList } from "@/features/common/components/avatar/StackedAvatarList";
import { HeartIcon } from "@/features/common/components/icons/HeartIcon";
import { TouchableView } from "@/features/common/components/tailwind/TouchableView";
import { Eth721Asset } from "@/features/nft/types";
import { ImageCardLikeButton } from "@/features/post/components/postCard/components/ImageCardLikeButton";
import { ImageCardRemoveLikeButton } from "@/features/post/components/postCard/components/ImageCardRemoveLikeButton";
import { ImageCardProps } from "@/features/post/components/postCard/type";
import { PostInfoWithLoggedInUserQuery } from "@/generated/generated-hooks";

import { CollectibleImageCardInformationSection } from "./CollectibleImageCardInformationSection";

type CollectibleImageCardProps = {
  actionButton: ReactNode;
  asset: Partial<Eth721Asset>;
  assetLoading: boolean;
  post: ImageCardProps["post"];
  postInfo: PostInfoWithLoggedInUserQuery;
};

/** @params postInfo is the data of usePostInfoWithLoggedInUserQuery */
export const CollectibleImageCard: FC<CollectibleImageCardProps> = (props) => {
  const { post, actionButton, assetLoading, asset, postInfo } = props;
  const isBid = false;

  // 3. check if the component serves as preview when createPost,
  // if yes, return a preview image
  const router = useRouter();
  const { route } = router;
  const isPreview = route === `${RouteName.NewPost}/[slug]`;

  const onClickToPreview = () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.ImageCardClickImage, {
      postSlug: post.slug,
      postId: post._id,
    });

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, preview: post.slug },
      },
      undefined,
      { shallow: true },
    );
  };

  return (
    <div
      className={`bg-${
        isPreview ? "white" : "transparent"
      } select-none flex flex-col w-full relative border border-primary rounded-xl overflow-hidden`}
      onContextMenu={(e) => e.preventDefault()}
      data-test-id="CollectibleImageCard-container"
      data-test-type="imageCard"
    >
      {/* top-left ribbon */}
      <div className="z-10 absolute top-0 left-0">
        <div className="ribborn-tl" />
      </div>
      {/* ImageArea */}
      <div className="relative">
        <TouchableView onClick={onClickToPreview}>
          <Image
            alt={post.title}
            width={post.previewImage.intrinsicSize.width}
            height={post.previewImage.intrinsicSize.height}
            src={
              postInfo?.post
                ? postInfo.post.images[0].url
                : post.previewImage.url
            }
            layout="responsive"
            onMouseDown={(e) => e.preventDefault()}
            priority
          />
        </TouchableView>

        {/* Stacked Avatar List */}
        <div className="absolute -bottom-6 left-3">
          <div className="flex -space-x-6">
            <StackedAvatarList
              avatars={
                // TODO: handle isBid - future
                isBid
                  ? [
                      { alt: post.title, src: post.previewImage.url },
                      { alt: post.title, src: post.previewImage.url },
                      { alt: post.title, src: post.previewImage.url },
                    ]
                  : [
                      {
                        walletAccount: asset?.owner,
                      },
                    ]
              }
            />
          </div>
        </div>
      </div>

      {/* Information */}
      <CollectibleImageCardInformationSection
        actionButton={actionButton}
        asset={asset}
        assetLoading={assetLoading}
        post={post}
      />

      {/* Utilities and Multi image indicator. */}
      <div className="absolute top-0 right-0 z-30 p-4">
        {postInfo && postInfo.post?.hasCurrentUserLike ? (
          <ImageCardRemoveLikeButton
            postId={post._id}
            logEvent={AnalyticsEventEnum.NftImageCardUnlike}
          >
            <div className="bg-gray-800 p-2 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
              <HeartIcon className="text-primary" />
            </div>
          </ImageCardRemoveLikeButton>
        ) : (
          <ImageCardLikeButton
            postId={post._id}
            logEvent={AnalyticsEventEnum.NftImageCardLike}
          >
            <div className="bg-gray-800 p-2 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
              <HeartIcon className="text-white" />
            </div>
          </ImageCardLikeButton>
        )}
      </div>
    </div>
  );
};
